import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/cn';

/**
 * Slim, monochrome notice/nudge BAR — icon + message + optional action, the shared shell behind
 * the in-stream reminders (out-of-credits, storage-full, the retention upgrade nudge) so they all
 * look identical instead of each re-rolling `rounded-xl border … bg-secondary-50 …`. Pass `to` to
 * make the WHOLE bar a link; otherwise put a button/link in `children`.
 */
// No text-size here — callers size their own content (text-sm / text-xs) to avoid a class clash.
const BAR = 'flex items-center gap-3 rounded-xl border border-secondary-200 dark:border-secondary-700 '
  + 'bg-secondary-50 dark:bg-secondary-900/60 px-3 py-2';

export interface NoticeBarProps {
  icon: React.ReactNode;
  /** When set, the entire bar is a Link to this route (hover affordance included). */
  to?: string;
  className?: string;
  children: React.ReactNode;
  'data-testid'?: string;
}

const NoticeBar: React.FC<NoticeBarProps> = ({ icon, to, className, children, ...rest }) => {
  const content = (
    <>
      <span className="shrink-0 text-secondary-500 dark:text-secondary-400">{icon}</span>
      {children}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cn(BAR, 'text-secondary-600 dark:text-secondary-300 hover:border-secondary-400 transition-colors', className)} {...rest}>
        {content}
      </Link>
    );
  }
  return <div className={cn(BAR, className)} {...rest}>{content}</div>;
};

export default NoticeBar;
